import { Context } from '../context/Context';
import { Scene } from '../scene/Scene';
import { Camera } from '../camera';
import { DrawableObject, RenderMode } from './index';
import { createColorTexture } from './RenderTarget';

/**
 * Off-screen GPU picker. Renders the scene's non-instanced pickables (gizmo
 * handles, polygon overlays, light visualizers, manually placed scene
 * objects) into a dedicated FBO, identifies them by per-object color id,
 * and reads back the pixel under the cursor.
 *
 * Instanced groups (bulk scattered foliage, props) are deliberately skipped
 * — touching the picking pass with thousands of trees is what made hover
 * picking dominate the CPU budget on the demo scene. Pickable instance-
 * grouped objects would need a future per-instance interaction system; if
 * you need that, render them here separately.
 *
 * Non-pickable opaques still occlude pickables correctly because the depth
 * buffer is seeded from the deferred renderer's G-buffer.
 */
export class PickingSceneRenderer {
  private buffer!: WebGLFramebuffer;
  private depthBuffer!: WebGLRenderbuffer;
  private colorTexture!: WebGLTexture;
  private ctx!: Context;
  private bufferWidth = 0;
  private bufferHeight = 0;

  public init(ctx: Context) {
    this.ctx = ctx;

    const gl = ctx.gl;
    const width = ctx.viewPort.width;
    const height = ctx.viewPort.height;
    this.bufferWidth = width;
    this.bufferHeight = height;

    this.colorTexture = createColorTexture(
      gl,
      width,
      height,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      gl.NEAREST,
    );

    const framebuffer = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      this.colorTexture,
      0,
    );

    this.depthBuffer = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.depthBuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, width, height);
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.RENDERBUFFER,
      this.depthBuffer,
    );

    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      throw new Error('Picking framebuffer incomplete');
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.buffer = framebuffer;
  }

  public resize(): void {
    if (!this.ctx) return;
    this.destroy();
    this.init(this.ctx);
  }

  public destroy(): void {
    const gl = this.ctx.gl;
    if (this.buffer) gl.deleteFramebuffer(this.buffer);
    if (this.depthBuffer) gl.deleteRenderbuffer(this.depthBuffer);
    if (this.colorTexture) gl.deleteTexture(this.colorTexture);
  }

  public pick(scene: Scene, camera: Camera, x: number, y: number): DrawableObject | undefined {
    const ctx = this.ctx;
    const gl = ctx.gl;
    const width = this.bufferWidth;
    const height = this.bufferHeight;

    const visible = scene.objectManager.getAllVisibleObjects(RenderMode.Picking, camera.frustum);

    // Drop instance-grouped objects — see class comment.
    const im = scene.instanceManager;
    const pickables: DrawableObject[] = [];
    for (const obj of visible) {
      if (!im.isManaged(obj)) pickables.push(obj);
    }

    // Fast path: nothing to pick. Skip the GPU pass entirely and the
    // ~1ms readPixels stall that goes with it.
    if (pickables.length === 0) return undefined;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.buffer);
    gl.viewport(0, 0, width, height);

    // Seed depth from the deferred renderer's G-buffer so non-pickable
    // opaques (walls, terrain, foliage) still occlude pickables — a click on
    // a wall mustn't pick the interactive behind it. Falls back to a depth
    // clear when the deferred pipeline isn't available.
    const depthSeeded = this.tryBlitGBufferDepth(gl, this.buffer, width, height);
    gl.clear(depthSeeded ? gl.COLOR_BUFFER_BIT : gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    // When depth is seeded from the G-buffer, a pickable with its own visible
    // geometry (e.g. a placed scene object) is *also* in the G-buffer — so
    // re-rasterizing it here produces depth values bit-identical to the seed.
    // The default reversed-Z `GREATER` then discards every picking fragment
    // ("equal Z is not strictly greater"), and clicks on placed objects appear
    // intermittent. Switching to `GEQUAL` lets the equal case through;
    // non-pickables still occlude pickables behind them because their depth
    // is strictly larger (closer under reversed-Z).
    if (depthSeeded) gl.depthFunc(gl.GEQUAL);

    const map = new Map<number, DrawableObject>();
    let idx = 1;
    for (const obj of pickables) {
      map.set(idx, obj);
      obj.render(ctx, camera, {
        mode: RenderMode.Picking,
        lights: [],
        objects: pickables,
        pickingReference: idx,
      });
      idx++;
    }

    if (depthSeeded) gl.depthFunc(gl.GREATER);

    const px = Math.max(0, Math.min(width - 1, Math.floor(x)));
    const py = Math.max(0, Math.min(height - 1, Math.floor(y)));
    const colorPicked = new Uint8Array(4);
    gl.readPixels(px, py, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, colorPicked);
    const pickedIdx = colorPicked[0] | (colorPicked[1] << 8) | (colorPicked[2] << 16);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    return map.get(pickedIdx);
  }

  private tryBlitGBufferDepth(
    gl: WebGL2RenderingContext,
    targetFramebuffer: WebGLFramebuffer,
    width: number,
    height: number,
  ): boolean {
    const deferred = this.ctx.engine.getDeferredRenderer();
    const gBuffer = deferred?.gBuffer;
    if (!gBuffer || gBuffer.width !== width || gBuffer.height !== height) return false;

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, gBuffer.framebuffer);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, targetFramebuffer);
    gl.blitFramebuffer(
      0, 0, width, height,
      0, 0, width, height,
      gl.DEPTH_BUFFER_BIT,
      gl.NEAREST,
    );
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, targetFramebuffer);
    return true;
  }
}
